import React, { useState, useEffect } from 'react';
import { FaSpinner, FaExclamationCircle, FaArrowDown, FaCheckCircle, FaHourglass } from 'react-icons/fa';
import axios from 'axios';
import { format } from 'date-fns';

const DepositRecord = ({ userDetails, token }) => {
  const [deposits, setDeposits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Styles
  const styles = {
    container: {
      backgroundColor: '#0F172A',
      color: '#E2E8F0',
      borderRadius: '12px',
      padding: '16px',
      fontFamily: 'Inter, system-ui, sans-serif',
    },
    header: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#F8FAFC',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    summary: {
      backgroundColor: '#1E293B',
      borderRadius: '8px',
      padding: '12px 16px',
      marginBottom: '20px',
      display: 'flex',
      justifyContent: 'space-between',
    },
    summaryItem: {
      textAlign: 'center',
    },
    summaryLabel: {
      fontSize: '12px',
      color: '#94A3B8',
      marginBottom: '4px',
    },
    summaryValue: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#F8FAFC',
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
    card: {
      backgroundColor: '#1E293B',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      border: '1px solid #334155',
      transition: 'transform 0.2s ease',
    },
    cardHeader: {
      backgroundColor: 'rgba(15, 23, 42, 0.5)',
      padding: '12px 16px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    amountContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    depositIcon: {
      color: '#10B981',
      fontSize: '18px',
    },
    amountInfo: {
      display: 'flex',
      flexDirection: 'column',
    },
    amount: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#F8FAFC',
      display: 'flex',
      alignItems: 'center',
    },
    currency: {
      fontSize: '13px',
      color: '#94A3B8',
      marginRight: '4px',
    },
    date: {
      fontSize: '12px',
      color: '#94A3B8',
    },
    statusBadge: {
      padding: '6px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    completedStatus: {
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      color: '#10B981',
    },
    pendingStatus: {
      backgroundColor: 'rgba(234, 179, 8, 0.1)',
      color: '#EAB308',
    },
    cardBody: {
      padding: '16px',
    },
    detailRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '10px',
    },
    detailLabel: {
      fontSize: '13px',
      color: '#94A3B8',
    },
    detailValue: {
      fontSize: '13px',
      color: '#F8FAFC',
      fontWeight: '500',
      wordBreak: 'break-all',
    },
    receiptValue: {
      backgroundColor: 'rgba(15, 23, 42, 0.5)',
      padding: '4px 8px',
      borderRadius: '4px',
      fontFamily: 'monospace',
      fontSize: '12px',
    },
    message: {
      marginTop: '12px',
      padding: '10px 12px',
      borderRadius: '8px',
      fontSize: '13px',
    },
    messageCompleted: {
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      color: '#10B981',
    },
    messagePending: {
      backgroundColor: 'rgba(234, 179, 8, 0.1)',
      color: '#EAB308',
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px 0',
      color: '#94A3B8',
    },
    loaderContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 0',
    },
    spinner: {
      fontSize: '24px',
      color: '#38BDF8',
      animation: 'spin 1s linear infinite',
      marginBottom: '12px',
    },
    loaderText: {
      fontSize: '14px',
      color: '#94A3B8',
    },
    errorContainer: {
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      borderRadius: '8px',
      padding: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    errorIcon: {
      fontSize: '20px',
      color: '#EF4444',
    },
    errorText: {
      fontSize: '14px',
      color: '#F8FAFC',
    },
  };

  useEffect(() => {
    const fetchDeposits = async () => {
      try {
        const phoneNumber = userDetails.mobile;
        const response = await axios.get(
          `https://api.globallequityfinance.com/api/deposits/${phoneNumber}`,
          { headers: { 'Authorization': token } }
        );
        setDeposits(response.data);
      } catch (err) {
        setError('Failed to load deposit records');
        console.error('Error fetching deposits:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDeposits();
  }, [userDetails.mobile, token]);

  const getStatusIcon = (status) => {
    return status.toLowerCase() === 'completed' 
      ? <FaCheckCircle /> 
      : <FaHourglass />;
  };

  const getStatusStyle = (status) => {
    return status.toLowerCase() === 'completed' 
      ? styles.completedStatus 
      : styles.pendingStatus;
  };

  const getMessageStyle = (status) => {
    return status.toLowerCase() === 'completed' 
      ? styles.messageCompleted 
      : styles.messagePending;
  };

  const getStatusMessage = (status) => {
    return status.toLowerCase() === 'completed' 
      ? 'Your deposit has been processed successfully and added to your wallet.' 
      : 'Your deposit is being processed. This usually takes just a few minutes.';
  };

  // Calculate total deposited amount
  const getTotalDeposited = () => {
    return deposits
      .filter(d => d.status.toLowerCase() === 'completed')
      .reduce((sum, d) => sum + d.amount, 0);
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loaderContainer}>
          <FaSpinner style={styles.spinner} />
          <p style={styles.loaderText}>Loading deposit history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <FaExclamationCircle style={styles.errorIcon} />
          <p style={styles.errorText}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      
      {deposits.length > 0 && (
        <div style={styles.summary}>
          <div style={styles.summaryItem}>
            <div style={styles.summaryLabel}>Total Deposits</div>
            <div style={styles.summaryValue}>{deposits.length}</div>
          </div>
          <div style={styles.summaryItem}>
            <div style={styles.summaryLabel}>Total Deposited</div>
            <div style={styles.summaryValue}>KES {getTotalDeposited().toLocaleString()}</div>
          </div>
        </div>
      )}
      
      <div style={styles.content}>
        {deposits.length === 0 ? (
          <div style={styles.emptyState}>
            <p>No deposit records found</p>
          </div>
        ) : (
          deposits.map((deposit) => (
            <div key={deposit.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <div style={styles.amountContainer}>
                  <FaArrowDown style={styles.depositIcon} />
                  <div style={styles.amountInfo}>
                    <div style={styles.amount}>
                      <span style={styles.currency}>KES</span>
                      {deposit.amount.toLocaleString()}
                    </div>
                    <div style={styles.date}>
                      {format(new Date(deposit.transactionDate), 'MMM d, yyyy • h:mm a')}
                    </div>
                  </div>
                </div>
                <div 
                  style={{
                    ...styles.statusBadge,
                    ...getStatusStyle(deposit.status)
                  }}
                >
                  {getStatusIcon(deposit.status)} {deposit.status}
                </div>
              </div>
              
              <div style={styles.cardBody}>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>M-Pesa Receipt</span>
                  <span style={styles.receiptValue}>{deposit.mpesaReceiptNumber}</span>
                </div>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Phone Number</span>
                  <span style={styles.detailValue}>{deposit.phoneNumber}</span>
                </div>
                <div 
                  style={{
                    ...styles.message,
                    ...getMessageStyle(deposit.status)
                  }}
                >
                  {getStatusMessage(deposit.status)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default DepositRecord;